import React from 'react';
import {
  Dialog,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Widget } from '@typeform/embed-react';

const TYPEFORM_ID = 'HmF80vTd';

interface AddPromptDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  petName: string;
  petParentEmail: string;
  animalId: string;
  // 1-indexed position of the prompt inside the album (e.g. "First Nap" is 3).
  promptNumber: number;
}

/**
 * Embeds the Petcademy Welcome Home Typeform full-screen so the adopter can
 * upload a photo + caption for the active prompt. We pass identifying info
 * via Typeform `hidden` fields (rendered as URL hash params, e.g.
 * `#name=Bella&email=...&prompt_number=3`) so the Typeform webhook can attach
 * the response to the right prompt without the user re-typing IDs.
 *
 * Submit handling: Typeform fires `onSubmit` immediately when the form is
 * submitted (before the redirect/thank-you screen). We invoke `onSubmitted`
 * so the parent can flip the prompt into its `submitting` state, then close.
 */
function AddPromptDialog({
  open,
  onClose,
  onSubmitted,
  petName,
  petParentEmail,
  animalId,
  promptNumber,
}: AddPromptDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = () => {
    onSubmitted();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: { xs: '100%', md: '90vh' },
          m: { xs: 0, md: 2 },
          borderRadius: { xs: 0, md: 2 },
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <IconButton
          onClick={onClose}
          aria-label="close typeform"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            backgroundColor: 'rgba(255,255,255,0.85)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
          }}
        >
          <Close />
        </IconButton>
        {open && (
          <Widget
            id={TYPEFORM_ID}
            inlineOnMobile
            onSubmit={handleSubmit}
            hidden={{
              name: petName,
              email: petParentEmail,
              prompt_number: String(promptNumber),
            }}
            style={{
              height: '100%',
              width: '100%',
              border: 'none',
            }}
          />
        )}
      </Box>
    </Dialog>
  );
}

export default AddPromptDialog;
